import React from 'react';
import { cn } from '../lib/utils';
import type { ThemeConfig } from '../schema';
import { themeToCssVars, themeToWrapperStyles, type ContentThemeSkin } from '../theme';

export interface ContentWrapperProps {
    children: React.ReactNode;
    className?: string;
    theme?: ThemeConfig;
    padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    border?: boolean;
    rounded?: boolean;
    shadow?: boolean;
    style?: React.CSSProperties;
    /**
     * EAA-1677: stamped as `data-skin` on this wrapper's root so embeds/iframes
     * without `:root` access still get playful-skin CSS — `undefined`/`'default'`
     * renders no attribute at all (inherits an ancestor's `data-skin`, if any).
     */
    skin?: ContentThemeSkin;
}

/**
 * ContentWrapper - Base wrapper component for content types
 * Applies theme via CSS custom properties that cascade to all children
 * Theme visual styles are applied to the outer wrapper only
 */
export function ContentWrapper({
    children,
    className,
    theme,
    padding = 'sm',
    border = true,
    rounded = true,
    shadow = false,
    style,
    skin,
}: ContentWrapperProps): React.ReactElement {
    const paddingClasses = {
        none: '',
        sm: 'p-3 sm:p-4',
        md: 'p-4 sm:p-5',
        lg: 'p-5 sm:p-6 md:px-8 md:py-7',
        xl: 'p-6 sm:p-8 md:p-10',
    };

    // EAA-1672: route through the shared theme module (Phase 1) instead of a
    // local hexToHsl + inline var/style maps, so ContentWrapper and
    // ContentWrapperFull emit CSS vars through one code path.
    const cssVariables = themeToCssVars(theme);
    const visualStyles = { ...themeToWrapperStyles(theme), ...style };

    return (
        <div
            className={cn(
                'content-wrapper',
                paddingClasses[padding],
                border && 'border border-border',
                rounded && 'rounded-lg',
                shadow && 'shadow-sm',
                className
            )}
            style={{
                ...cssVariables,
                ...visualStyles,
            }}
            data-skin={skin && skin !== 'default' ? skin : undefined}
        >
            {children}
        </div>
    );
}
